import type { ManualArElement, ManualBody } from "@arc/contracts";

/**
 * The WebGL element panel's URL contract (ADR-044 D1).
 *
 * **These param names are NOT ours to choose.** Unity builds this URL and embeds our page; renaming them to the
 * app's `arc_*` convention would need a coordinated Unity release we cannot verify without its source — the same
 * compatibility rule that already binds mobile, which has its API base URL compiled in
 * (ENGINEERING-GUARDRAILS §5). So the legacy spelling stands: `?webgl=true&elementId=<id>&manualId=<id>`.
 *
 * Deliberately NOT part of `NavState`: every `arc_*` param is app navigation that pushes history and feeds the
 * drill-down reducer. This is a one-shot embed target read once at boot, so putting it there would drag an
 * embed-only concern into the navigator for no benefit.
 */
export interface WebglPanelTarget {
  elementId: number;
  /** ADR-044 D4 — REQUIRED for now. The legacy falls back to resolving the manual from the element
   *  (`LoadARElement`), but we have no equivalent: every AR element endpoint is manual-scoped and there is no
   *  `GET /elements/:id`. Inventing one means designing authz around an element we have not resolved yet, on a
   *  guess about what Unity sends. `null` here ⇒ the panel says so plainly instead of rendering blank. */
  manualId: number | null;
  /** The legacy's third argument, default true: whether to preselect the element's tab. `?elementSelected=false`
   *  opens the step WITHOUT activating a tab — the warm-open preload case. */
  elementSelected: boolean;
}

/** A positive int, or null. Rejects `1.5`, `-3`, `0`, `""`, `abc` and `12abc` — `Number()` (not `parseInt`) so a
 *  trailing-garbage id can never be silently truncated into a valid-looking one. */
function positiveInt(raw: string | null): number | null {
  if (raw === null || raw.trim() === "") return null;
  const n = Number(raw);
  return Number.isInteger(n) && n > 0 ? n : null;
}

/**
 * Parse the embed target out of a query string. Returns `null` when this is NOT the webgl route, which is the
 * signal the app boots normally.
 *
 * `webgl` must be exactly `true` (the legacy writes `?webgl=true`); any other value means someone hand-edited the
 * URL and we treat it as a normal app load rather than guessing.
 */
export function parseWebglPanel(search: string): WebglPanelTarget | null {
  const q = new URLSearchParams(search);
  if (q.get("webgl") !== "true") return null;
  const elementId = positiveInt(q.get("elementId"));
  if (elementId === null) return null; // the route is meaningless without a target element
  return {
    elementId,
    manualId: positiveInt(q.get("manualId")),
    // Only the exact string "false" turns it off, matching the legacy's `elementSelected === undefined ? true : …`.
    elementSelected: q.get("elementSelected") !== "false",
  };
}

/** Where an element lives in the body: on a step, or in the step-less "Alle Schritte" bucket (ADR-044 D8). */
export type WebglElementLocation =
  | {
      kind: "step";
      element: ManualArElement;
      siblings: ManualArElement[];
      stepId: number;
      stepName: string;
      stepText: string | null;
      sectionName: string;
    }
  | { kind: "all-steps"; element: ManualArElement; siblings: ManualArElement[] };

/**
 * Find the target element in EITHER bucket (ADR-044 D8).
 *
 * An element with no Section/Step membership is a legitimate "Alle Schritte" element — the API buckets it into
 * `allStepsElements`, and the legacy WebGL route detects exactly that case and branches to `FocusWebGLAllSteps`
 * rather than step focus. Searching only the steps would send a valid Unity URL to the unknown-element state for
 * content the legacy happily displays.
 *
 * Returns `null` only when the element is genuinely absent from this manual's body.
 */
export function locateWebglElement(body: ManualBody, elementId: number): WebglElementLocation | null {
  for (const section of body.sections) {
    for (const step of section.steps) {
      const element = step.elements.find((e) => e.id === elementId);
      if (element) {
        return {
          kind: "step",
          element,
          siblings: step.elements,
          stepId: step.id,
          stepName: step.name,
          stepText: step.text,
          sectionName: section.name,
        };
      }
    }
  }
  const allSteps = body.allStepsElements.find((e) => e.id === elementId);
  return allSteps ? { kind: "all-steps", element: allSteps, siblings: body.allStepsElements } : null;
}
